import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { entCreateBenefit, entCreateDeduction, entListBenefits, entListDeductions } from "../salaryEnterpriseApi";
import { useToast } from "../../../Context/ToastContext";
import DatePickerField from "../../../../components/common/DatePickerField";
import EmployeeSelect from "../../../../components/common/EmployeeSelect";

export default function SalaryBenefitsDeductions() {
  const toast = useToast();
  const [tab, setTab] = useState("benefits");
  const [benefits, setBenefits] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [benefitEmployeeId, setBenefitEmployeeId] = useState("");
  const [deductionEmployeeId, setDeductionEmployeeId] = useState("");
  const [benefitEffectiveFrom, setBenefitEffectiveFrom] = useState("");
  const [deductionEffectiveFrom, setDeductionEffectiveFrom] = useState("");

  async function load() {
    const [b, d] = await Promise.all([entListBenefits(), entListDeductions()]);
    setBenefits(b || []);
    setDeductions(d || []);
  }

  useEffect(() => {
    load().catch(() => {});
  }, []);

  async function addBenefit(e) {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await entCreateBenefit({
        employee_id: benefitEmployeeId,
        benefit_code: fd.get("benefit_code"),
        section: fd.get("section") || null,
        employee_amount: Number(fd.get("employee_amount") || 0),
        employer_amount: Number(fd.get("employer_amount") || 0),
        effective_from: fd.get("effective_from"),
      });
      toast.success("Benefit added");
      setBenefitEmployeeId("");
      setBenefitEffectiveFrom("");
      e.target.reset();
      await load();
    } catch (err) {
      toast.error(err?.detail || "Failed");
    }
  }

  async function addDeduction(e) {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await entCreateDeduction({
        employee_id: deductionEmployeeId,
        deduction_type: fd.get("deduction_type"),
        mode: fd.get("mode"),
        amount: Number(fd.get("amount") || 0),
        effective_from: fd.get("effective_from"),
        loan_meta: {},
      });
      toast.success("Deduction added");
      setDeductionEmployeeId("");
      setDeductionEffectiveFrom("");
      e.target.reset();
      await load();
    } catch (err) {
      toast.error(err?.detail || "Failed");
    }
  }

  return (
    <div className="bw-stack">
      <div className="flex gap-2">
        <button type="button" className={`bw-btn ${tab === "benefits" ? "bw-btn-primary" : "bw-btn-secondary"}`} onClick={() => setTab("benefits")}>
          <span className="bw-badge bw-badge-benefit" style={{ marginRight: 6 }} />
          Benefits (pre-tax)
        </button>
        <button type="button" className={`bw-btn ${tab === "deductions" ? "bw-btn-primary" : "bw-btn-secondary"}`} onClick={() => setTab("deductions")}>
          <span className="bw-badge bw-badge-deduction" style={{ marginRight: 6 }} />
          Deductions
        </button>
      </div>

      {tab === "benefits" ? (
        <>
          <form className="bw-form-grid" onSubmit={addBenefit}>
            <div className="bw-field xl-4">
              <EmployeeSelect
                label="Employee"
                required
                value={benefitEmployeeId}
                onChange={setBenefitEmployeeId}
                selectClassName="bw-input w-full"
              />
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Code</label>
              <input name="benefit_code" className="bw-input" defaultValue="NPS" required />
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Section</label>
              <input name="section" className="bw-input" defaultValue="80CCD" />
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Employee amount</label>
              <input name="employee_amount" type="number" className="bw-input" />
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Effective from</label>
              <input type="hidden" name="effective_from" value={benefitEffectiveFrom} />
              <DatePickerField
                hideLabel
                required
                value={benefitEffectiveFrom}
                onChange={setBenefitEffectiveFrom}
                inputClassName="bw-input"
              />
            </div>
            <div className="bw-field xl-12">
              <button type="submit" className="bw-btn bw-btn-primary">
                Add benefit
              </button>
            </div>
          </form>
          <div className="bw-table-wrap">
            <table className="bw-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Section</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {benefits.map((r) => (
                  <tr key={r.id}>
                    <td>{r.benefit_code}</td>
                    <td>{r.section}</td>
                    <td>{r.employee_amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <form className="bw-form-grid" onSubmit={addDeduction}>
            <div className="bw-field xl-4">
              <EmployeeSelect
                label="Employee"
                required
                value={deductionEmployeeId}
                onChange={setDeductionEmployeeId}
                selectClassName="bw-input w-full"
              />
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Type</label>
              <select name="deduction_type" className="bw-select">
                <option value="loan_recovery">Loan recovery</option>
                <option value="food">Food</option>
                <option value="attendance_penalty">Attendance penalty</option>
              </select>
              <p className="bw-hint" style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                Salary Advance Taken is managed under{" "}
                <Link to="/settings/allowances-benefits/advance-taken">Settings → Allowances & Benefits → Advance Taken</Link>
                , not as a recurring deduction here.
              </p>
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Mode</label>
              <select name="mode" className="bw-select">
                <option value="recurring">Recurring</option>
                <option value="one_time">One time</option>
                <option value="installment">Installment</option>
              </select>
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Amount</label>
              <input name="amount" type="number" className="bw-input" />
            </div>
            <div className="bw-field xl-4">
              <label className="bw-label">Effective from</label>
              <input type="hidden" name="effective_from" value={deductionEffectiveFrom} />
              <DatePickerField
                hideLabel
                required
                value={deductionEffectiveFrom}
                onChange={setDeductionEffectiveFrom}
                inputClassName="bw-input"
              />
            </div>
            <div className="bw-field xl-12">
              <button type="submit" className="bw-btn bw-btn-primary">
                Add deduction
              </button>
            </div>
          </form>
          <div className="bw-table-wrap">
            <table className="bw-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Mode</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {deductions.map((r) => (
                  <tr key={r.id}>
                    <td>{r.deduction_type}</td>
                    <td>{r.mode}</td>
                    <td>{r.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
